import { useMemo } from 'react';

import { ICartEntry } from 'features/cart/types';

import { ILegacyCartEntry } from '@rbi-ctg/menu';
import { selectors, useAppSelector } from 'state/global-state';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useIsLoyaltyEnabled } from 'state/loyalty/hooks/use-is-loyalty-enabled';
import { useLoyaltyUser } from 'state/loyalty/hooks/use-loyalty-user';

import { BestPromotionResult } from '../types';
import { parseCartEntries } from '../utils/parse-cart-entries';

import { useLegacyBestPromotion } from './use-legacy-best-promotion';
import { useLoyaltyServiceBestPromotion } from './use-loyalty-service-best-promotion';

// The component supplies raw cart/store context; parsing, loyalty state, and the
// active source are all resolved internally.
interface UseBestPromotionArgs {
  cartEntries?: ILegacyCartEntry[] | ICartEntry[];
  storeId?: string | null;
  skipStoreDayPartHours?: boolean;
  onActivateResolved?: () => void;
}

export function useBestPromotion({
  cartEntries,
  storeId,
  skipStoreDayPartHours,
  onActivateResolved,
}: UseBestPromotionArgs): BestPromotionResult {
  const serviceEnabled = useFlag(LaunchDarklyFlag.ENABLE_LOYALTY_SERVICE_PROMOTIONS);
  const loyaltyEnabled = useIsLoyaltyEnabled();
  const { loyaltyUser, loading: loyaltyLoading } = useLoyaltyUser();
  const loyaltyId = loyaltyUser?.id;

  const appliedRewards = useAppSelector(selectors.loyalty.selectAppliedLoyaltyRewards);
  const parsedCartEntries = useMemo(() => parseCartEntries(cartEntries, appliedRewards), [
    cartEntries,
    appliedRewards,
  ]);

  const sourceArgs = {
    cartEntries: parsedCartEntries,
    // Only send storeId when a cartEntries prop was provided (even an empty
    // array), independent of parsed contents — parity with the legacy path.
    storeId: cartEntries ? storeId : undefined,
    skipStoreDayPartHours,
    onActivateResolved,
    loyaltyId,
    loyaltyEnabled,
    loyaltyLoading,
  };
  const canQuery = loyaltyEnabled && Boolean(loyaltyId);

  // Both sources stay mounted for a stable hook order; each self-skips based on
  // `enabled`, so only the active source performs network work.
  const legacy = useLegacyBestPromotion({ ...sourceArgs, enabled: canQuery && !serviceEnabled });
  const service = useLoyaltyServiceBestPromotion({
    ...sourceArgs,
    enabled: canQuery && serviceEnabled,
  });

  return serviceEnabled ? service : legacy;
}
